import { assertNeverType, capitalCase, createError, formatMonthYearPeriod } from '@common/neo.tax';
import { css } from '@emotion/css';
import { Cell } from 'react-table';

import { BulkPriorYearTaxDocumentUpload, BulkTicketingDataUpload } from '@neo.tax/shared/hooks/useCompany';

import { ACCOUNTING_PROVIDER_NAMES, PAYROLL_PROVIDER_NAMES } from '../constants';
import {
  AccountingProvider,
  FileUpload,
  FileUploadTicketingProvider,
  GetCompanyQuery,
  GetSyncedProvidersQuery,
  PayrollProvider,
  RdBusinessComponentUploadFragment,
  RdBusinessComponentUploadStatus,
  SyncedAccountingProviders,
  SyncedPayrollMonthExpense,
  SyncedPayrollProviders,
  SyncedRdPayrollCost,
  TicketingProvider,
} from '../graphql';

export type ProviderUnion = AccountingProvider | PayrollProvider | TicketingProvider;

export type SyncedProviders = Omit<GetSyncedProvidersQuery['getFiscalYear']['syncedProviders'], 'id'>;

export type DataSourcesTableItem = {
  id: string;
  title: string;
  provider: ProviderUnion | 'csv' | 'unknown';
  providerLabel?: string;
  entity?: string;
  itemType:
    | 'accounting'
    | 'accounting-token'
    | 'payroll'
    | 'payroll-token'
    | 'ticketing-file'
    | 'tax-document'
    | 'timesheet'
    | 'asc-cost'
    | 'rd-payroll-cost' // For R&D Payroll Cost uploads (CSV uploads not payroll connections)
    | 'payroll-month-expense'
    | 'github-email-mapping'
    | 'rd-business-components-file';
  originalSyncDate: string;
  status: 'success' | 'failure' | 'pending' | 'validating' | 'saved';
  /** When set, the row's action renders as a disabled button with this label. */
  disabledActionText?: string;
  /** When set, the status pill shows this on hover. */
  statusDescription?: string;
};

type AccountingToken = GetCompanyQuery['getCompany']['accountingTokens'][number];
type PayrollToken = GetCompanyQuery['getCompany']['payrollTokens'][number];

export const accountingTokenToDataSourcesTableItem = (token: AccountingToken): DataSourcesTableItem => ({
  id: token.id,
  title: ACCOUNTING_PROVIDER_NAMES[token.accountingProvider],
  provider: token.accountingProvider,
  itemType: 'accounting-token',
  originalSyncDate: token.createdAt,
  status: 'saved',
});

export const payrollTokenToDataSourcesTableItem = (token: PayrollToken): DataSourcesTableItem => ({
  id: token.id,
  title: PAYROLL_PROVIDER_NAMES[token.payrollProvider],
  provider: token.payrollProvider,
  itemType: 'payroll-token',
  originalSyncDate: token.createdAt,
  status: 'saved',
});

const TICKETING_PROVIDER_MAP: Record<FileUploadTicketingProvider, string> = {
  [FileUploadTicketingProvider.Asana]: 'Asana',
  [FileUploadTicketingProvider.AzureDevops]: 'Azure DevOps',
  [FileUploadTicketingProvider.GithubIssues]: 'GitHub',
  [FileUploadTicketingProvider.JiraCloud]: 'Jira Cloud',
  [FileUploadTicketingProvider.JiraDataCenter]: 'Jira Data Center',
  [FileUploadTicketingProvider.Linear]: 'Linear',
  [FileUploadTicketingProvider.Shortcut]: 'Shortcut',
  [FileUploadTicketingProvider.Trello]: 'Trello',
  [FileUploadTicketingProvider.Other]: 'Other',
};

export const getTicketingProviderName = (ticketingProvider: FileUploadTicketingProvider): string =>
  TICKETING_PROVIDER_MAP[ticketingProvider];

const getFileValidationStatus = (fileUpload: { isValid?: boolean | null }): DataSourcesTableItem['status'] => {
  if (typeof fileUpload.isValid !== 'boolean') {
    return 'validating';
  }
  return fileUpload.isValid ? 'success' : 'failure';
};

const getSyncedPayrollMonthExpenseStatus = (
  syncedPayrollMonthExpense: SyncedPayrollMonthExpense,
): DataSourcesTableItem['status'] => {
  const { fileUpload } = syncedPayrollMonthExpense;
  if (!fileUpload) {
    // When there's no fileUpload, that means ASC cost file has been imported before we started validating it with one schema
    return 'success';
  }
  if (typeof fileUpload.isValid !== 'boolean') {
    // When fileUpload exists but isValid is not yet determined, that means the file is being ingested
    return 'pending';
  }
  return fileUpload.isValid ? 'success' : 'failure';
};

export const dataSourcesTableItemMapper = (
  provider: SyncedAccountingProviders | SyncedPayrollProviders,
): DataSourcesTableItem => {
  const providerName = getProviderName(provider);
  return {
    id: provider.id,
    title: providerName,
    provider: provider.providerName,
    providerLabel: providerName,
    itemType: getProviderType(provider),
    originalSyncDate: provider.originalSyncDate,
    status: 'success',
  };
};

export const bulkTicketingFileUploadToDataSourcesTableItem = (
  fileUpload: BulkTicketingDataUpload,
): DataSourcesTableItem => ({
  id: fileUpload.id,
  title: fileUpload.fileName,
  provider: 'unknown',
  providerLabel: getTicketingProviderName(fileUpload.ticketingProvider),
  entity: capitalCase(fileUpload.ticketingEntityType),
  itemType: 'ticketing-file',
  originalSyncDate: fileUpload.createdAt,
  status: getFileValidationStatus(fileUpload),
});

export const bulkPriorYearTaxDocumentToDataSourcesTableItem = (
  fileUpload: BulkPriorYearTaxDocumentUpload,
): DataSourcesTableItem => ({
  id: fileUpload.id,
  title: `${fileUpload.fileName}`,
  provider: 'unknown',
  itemType: 'tax-document',
  originalSyncDate: fileUpload.createdAt,
  status: 'success',
});

export const timesheetUploadToDataSourcesTableItem = (fileUpload: FileUpload): DataSourcesTableItem => ({
  id: fileUpload.id,
  title: `${fileUpload.fileName}`,
  provider: 'unknown',
  itemType: 'timesheet',
  originalSyncDate: fileUpload.createdAt,
  status: 'success',
});

export const githubEmailMappingUploadToDataSourcesTableItem = (fileUpload: FileUpload): DataSourcesTableItem => ({
  id: fileUpload.id,
  title: fileUpload.fileName,
  provider: TicketingProvider.GithubIssues,
  providerLabel: 'GitHub',
  itemType: 'github-email-mapping',
  originalSyncDate: fileUpload.createdAt,
  status: getFileValidationStatus(fileUpload),
});

export const syncedPayrollMonthExpenseToDataSourcesTableItem = (
  syncedPayrollMonthExpense: SyncedPayrollMonthExpense,
): DataSourcesTableItem => {
  const { end, start } = formatMonthYearPeriod(syncedPayrollMonthExpense.period);
  const status = getSyncedPayrollMonthExpenseStatus(syncedPayrollMonthExpense);
  return {
    id: syncedPayrollMonthExpense.id,
    title: `ASC 350-40 Cost CSV Upload (${start} - ${end})`,
    provider: 'csv',
    providerLabel: 'CSV',
    itemType: 'payroll-month-expense',
    originalSyncDate: syncedPayrollMonthExpense.originalSyncDate,
    status,
  };
};

export const syncedRdPayrollCostToDataSourcesTableItem = (
  syncedRdPayrollCost: SyncedRdPayrollCost,
): DataSourcesTableItem => {
  const { fileUpload, provider } = syncedRdPayrollCost;
  // Show 'csv' for self-reported uploads, otherwise show the actual provider (e.g., Rippling logo)
  const providerDisplay = provider === PayrollProvider.SelfReported ? 'csv' : provider;
  return {
    id: syncedRdPayrollCost.id,
    title: fileUpload.fileName,
    provider: providerDisplay,
    providerLabel: provider === PayrollProvider.SelfReported ? 'CSV' : PAYROLL_PROVIDER_NAMES[provider],
    itemType: 'rd-payroll-cost',
    originalSyncDate: syncedRdPayrollCost.originalSyncDate,
    status: getFileValidationStatus(fileUpload),
  };
};

const RD_BUSINESS_COMPONENT_UPLOAD_STATUS: Record<RdBusinessComponentUploadStatus, DataSourcesTableItem['status']> = {
  [RdBusinessComponentUploadStatus.Pending]: 'validating',
  [RdBusinessComponentUploadStatus.Accepted]: 'success',
  [RdBusinessComponentUploadStatus.Rejected]: 'failure',
};

/** Why the row cannot be deleted: its list is in use, or its import has not decided yet. */
const getRdBusinessComponentUploadDisabledActionText = (
  upload: RdBusinessComponentUploadFragment,
): string | undefined => {
  if (upload.isInUse) {
    return 'In use';
  }
  if (upload.status === RdBusinessComponentUploadStatus.Pending) {
    return 'Validating';
  }
  return undefined;
};

/** The row's id is the upload's file upload id, the one id every upload has, accepted or not. */
export const rdBusinessComponentUploadToDataSourcesTableItem = (
  upload: RdBusinessComponentUploadFragment,
): DataSourcesTableItem => ({
  id: upload.id,
  title: upload.fileUpload.fileName,
  provider: 'csv',
  itemType: 'rd-business-components-file',
  originalSyncDate: upload.fileUpload.createdAt,
  status: RD_BUSINESS_COMPONENT_UPLOAD_STATUS[upload.status],
  statusDescription: upload.rejectionMessage ?? undefined,
  disabledActionText: getRdBusinessComponentUploadDisabledActionText(upload),
});

export const getDataSourceTypeLabel = (itemType: DataSourcesTableItem['itemType']): string => {
  switch (itemType) {
    case 'accounting':
      return 'Accounting';
    case 'accounting-token':
      return 'Accounting connection';
    case 'payroll':
    case 'rd-payroll-cost':
      return 'Payroll';
    case 'payroll-token':
      return 'Payroll connection';
    case 'ticketing-file':
      return 'Ticketing Data Upload';
    case 'tax-document':
      return 'Prior Year Tax Document';
    case 'timesheet':
      return 'Timesheet';
    case 'asc-cost':
      return 'ASC 350-40 Cost';
    case 'payroll-month-expense':
      return 'ASC 350-40 Cost';
    case 'github-email-mapping':
      return 'GitHub Email Mapping';
    case 'rd-business-components-file':
      return 'Business Components';
  }
};

/**
 * Encapsulating type casting here so we don't have to do it in other places in the code.
 * If we know the provider type then we can reliably infer the provider enum.
 */
export const getProviderName = (provider: SyncedAccountingProviders | SyncedPayrollProviders): string => {
  const providerType = getProviderType(provider);
  switch (providerType) {
    case 'accounting':
      return ACCOUNTING_PROVIDER_NAMES[provider.providerName as AccountingProvider];
    case 'payroll':
      return PAYROLL_PROVIDER_NAMES[provider.providerName as PayrollProvider];
    default:
      return assertNeverType(providerType, 'Provider type');
  }
};

export const getProviderType = (
  provider: SyncedAccountingProviders | SyncedPayrollProviders,
): 'accounting' | 'payroll' => {
  switch (provider.__typename) {
    case 'SyncedAccountingProviders':
      return 'accounting';
    case 'SyncedPayrollProviders':
      return 'payroll';
    default:
      throw createError('SyncedProviderTypeError', 'Provider type not found');
  }
};

const condensedRowClassName = css({ paddingTop: '0.5rem', paddingBottom: '0.5rem', height: 52 });
export const dataSourceTableCellClassNames = (_: Cell<DataSourcesTableItem>): Record<string, boolean>[] => [
  {
    [condensedRowClassName]: true,
  },
];
